import React, { useState, useEffect } from 'react'
import { map, indexOf, find, get, isNull } from 'lodash'
import { SortableContainer, SortableElement } from 'react-sortable-hoc'
import arrayMove from 'array-move'
import { Delete } from '../../../../../../components/Icons'
import { resizeImage } from '../../../../../../utils'

const SortableItem = SortableElement(({lead, saveSubmission, sectionIndex, imageIndex, id, value, style}) => {
  const [isHovered, setIsHovered] = useState(false)
  // STY-625: resolves the image's position in the document being written rather
  // than in the one this row was rendered from. A delete that lands after some
  // other change used to remove whatever sat at the old index.
  const deleteImage = (lead, id, sectionIndex, imageIndex, saveSubmission) => {
    saveSubmission(doc => {
      if (lead) {
        doc.leadImage = null
      } else {
        const images = doc.sections[sectionIndex].images[imageIndex].images
        const index = indexOf(images, id)
        // Guarded, because splice(-1, 1) removes the LAST image: a lookup miss
        // used to silently delete a placement nobody clicked.
        if (index !== -1) images.splice(index, 1)
      }
      return doc
    })
  }

  return (
    <div
      // STY-637: the grab cursor is the only thing telling an editor these tiles
      // reorder. Withheld from the lead image, which is a single disabled slot —
      // offering a grab that does nothing is worse than offering none.
      className={lead ? undefined : 'placed-image'}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ ...style, position: 'relative', display: 'inline-block' }}
    >
      <img draggable={false} style={{ ...style, margin: 0 }} src={resizeImage(value, 300)} alt='' />
      {isHovered && (
        <Delete 
          style={{ cursor: 'pointer', position: 'absolute', top: -10, right: -5 }}
          onClick={() => deleteImage(lead, id, sectionIndex, imageIndex, saveSubmission)}
          scale={2}
          color='#ECB595'
        />
      )}
    </div>
  )
})

const SortableList = SortableContainer(({lead, saveSubmission, sectionIndex, imageIndex, images, gallery}) => {
  return (
    <div>
      {map(images, (image, i) => {
        const galleryImage = find(gallery.images, ['id', image])
        if (!galleryImage) return null
        return (
          <SortableItem
            lead={lead}
            disabled={lead || false}
            saveSubmission={saveSubmission}
            sectionIndex={sectionIndex}
            imageIndex={imageIndex}
            id={image}
            value={`https://smp-is.stylemepretty.com/${get(gallery, 'path')}/${galleryImage.filename}`}
            style={{ width: 141.66, marginTop: 10, marginLeft: i % 3 === 1 ? 10 : 0, marginRight: i % 3 === 1 ? 10 : 0 }}
            index={i}
            key={i}
          />
        )
      })}
    </div>
  )
})

// STY-625: the reorder replaces one array on the current document. It used to
// post a whole captured document, so a sort that landed after a drop reverted
// the drop.
const sortImages = (saveSubmission, sectionIndex, imageIndex, images) => {
  saveSubmission(doc => {
    doc.sections[sectionIndex].images[imageIndex].images = images
    return doc
  })
}

const ImageContainer = ({ sectionIndex, imageIndex, gallery, saveSubmission, additionalInfo, lead }) => {
  const [items, setItems] = useState(lead ? (isNull(additionalInfo.leadImage) ? [] : [get(additionalInfo, 'leadImage')]) : get(additionalInfo, `sections[${sectionIndex}].images[${imageIndex}].images`, []))
  const onSortEnd = ({ oldIndex, newIndex }) => {
    if(oldIndex !== newIndex) {
      setItems(arrayMove(items, oldIndex, newIndex))
      sortImages(saveSubmission, sectionIndex, imageIndex, arrayMove(items, oldIndex, newIndex))
    }
  }
  useEffect(() => {
    setItems(lead ? (isNull(additionalInfo.leadImage) ? [] : [get(additionalInfo, 'leadImage')]) : get(additionalInfo, `sections[${sectionIndex}].images[${imageIndex}].images`, []))
  }, [lead, additionalInfo, sectionIndex, imageIndex])
  if (items.length) {
    return (
      <SortableList
        lead={lead}
        saveSubmission={saveSubmission}
        sectionIndex={lead ? null : sectionIndex}
        imageIndex={lead ? null : imageIndex}
        images={items}
        gallery={gallery}
        pressDelay={150}
        helperClass='placed-image--dragging'
        axis='xy'
        onSortEnd={onSortEnd}
      />
    )
  }
  return <div />
}

export default ImageContainer